export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

const CONSENT_KEY = 'fairplay.analytics-consent';
const CONSENT_EVENT = 'fairplay:analytics-consent';
type AnalyticsConsent = 'granted' | 'denied';

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: any[]) => void;
  }
}

const readConsent = (): AnalyticsConsent | null => {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer!.push(arguments);
  };
  return window.gtag;
};

const loadGoogleAnalytics = () => {
  if (!GA_MEASUREMENT_ID || document.getElementById('fairplay-ga4')) return;
  const script = document.createElement('script');
  script.id = 'fairplay-ga4';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  const gtag = ensureGtag();
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
};

const clearAnalyticsCookies = () => {
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (!name.startsWith('_ga')) return;
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${name}=; Max-Age=0; path=/; domain=.fairplayed.app; SameSite=Lax`;
  });
};

export const initializeAnalytics = () => {
  if (!GA_MEASUREMENT_ID) return;
  const gtag = ensureGtag();
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  if (readConsent() === 'granted') {
    gtag('consent', 'update', { analytics_storage: 'granted' });
    loadGoogleAnalytics();
  }
};

export const setAnalyticsConsent = (consent: AnalyticsConsent) => {
  try { localStorage.setItem(CONSENT_KEY, consent); } catch { /* ignore */ }
  const gtag = ensureGtag();
  gtag('consent', 'update', { analytics_storage: consent });
  if (consent === 'granted') loadGoogleAnalytics();
  else clearAnalyticsCookies();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: consent }));
};

export const hasAnalyticsConsent = () => readConsent() === 'granted';
export const analyticsConsentEvent = CONSENT_EVENT;

export const trackPageView = (path: string, title: string) => {
  if (!GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;
  ensureGtag()('event', 'page_view', {
    page_title: title,
    page_location: `${window.location.origin}${path}`,
  });
};

export const trackEvent = (name: string, parameters: Record<string, string | number | boolean> = {}) => {
  if (!GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;
  ensureGtag()('event', name, parameters);
};

export const setupCookieConsent = () => {
  if (!GA_MEASUREMENT_ID || readConsent() !== null || document.getElementById('fairplay-cookie-banner')) return;

  const banner = document.createElement('aside');
  banner.id = 'fairplay-cookie-banner';
  banner.className = 'cookie-consent';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-labelledby', 'cookie-consent-title');
  banner.innerHTML = `
      <div>
        <strong id="cookie-consent-title">A small clubhouse cookie.</strong>
        <p>FairPlay uses essential storage to keep the app working. With your permission, Google Analytics helps us understand which pages are useful. No advertising tracking.</p>
        <a href="/privacy">Read the privacy policy</a>
      </div>
      <div class="cookie-consent-actions">
        <button type="button" data-consent="denied">Essential only</button>
        <button type="button" data-consent="granted">Allow analytics</button>
      </div>`;
  document.body.appendChild(banner);

  banner.querySelectorAll<HTMLButtonElement>('[data-consent]').forEach((button) => {
    button.addEventListener('click', () => {
      setAnalyticsConsent(button.dataset.consent as AnalyticsConsent);
      banner.remove();
    });
  });
};
